<script lang="ts" setup>
import { useSharedData, usePage } from '@consumer/composables/sharedData'
import { useIsPdfCapture } from '@consumer/composables/pdfCapture'
import { cssPropertiesForTheme, setCurrentTheme } from '@consumer/composables/themes'

import V2Header from '@consumer/modules/v2/layout/Header.vue'
import V2Footer from '@consumer/modules/v2/layout/Footer.vue'
import BaseLayout from '@consumer/layouts/BaseLayout.vue'
import { useScreen } from '@consumer/helpers/screen'

defineOptions({
  inheritAttrs: false,
})

useScreen()

const { currentUser, currentTheme } = $(useSharedData())

const { component, url } = $(usePage())
const isPdfCapture = useIsPdfCapture()

const theme = setCurrentTheme()

const headerType = computed(() => {
  if ([
    'MultiPlaceGiftIdea',
    'CheckoutOrderConfirmation',
    'CheckoutCart',
    'CalendarEvent',
  ].includes(component)
    || url.startsWith('/u/')
    || url.startsWith('/my-account')
    || url.startsWith('/login')
    || url.startsWith('/signup')
  ) {
    return 'product'
  }
  else if (component === 'Checkout') {
    return 'checkout'
  }
  else if (
    [
      'RecipientGiftCardAccess',
      'RecipientGiftCardActivate',
      'RecipientGiftCardBlocked',
      'RecipientGiftCardManage',
      'RecipientGiftLandingV2',
      'ResendUnlockInstructions',
      'UnlockAccount',
      'Unsubscribe',
      'ContactOptOut',
    ].includes(component)
    || url.startsWith('/support')
    || url.startsWith('/entercode')
    || url.startsWith('/card/activate')
    || url.startsWith('/calendar/invitations')
  ) {
    return 'simple'
  }
  else {
    return 'default'
  }
})

const footerType = computed(() => {
  if (component === 'RecipientGiftLandingV2') {
    return false
  }
  else if (
    component.startsWith('Checkout')
  ) {
    return 'checkout'
  }
  else if (url.startsWith('/card/manage') || url.startsWith('/card/access')) {
    return 'card_manage'
  }
  else if (
    url.startsWith('/support')
    || url.startsWith('/entercode')
    || url.startsWith('/card/activate')
    || component === 'Unsubscribe'
    || component === 'ContactOptOut'
  ) {
    return 'simple'
  }
  else {
    return 'default'
  }
})

</script>

<template>
  <Head>
    <title>Giftly: Online gift cards, printable gift cards, email gift cards, e-gift cards</title>
  </Head>

  <BaseLayout
    :key="`${headerType}-${Boolean(currentUser)}-${currentTheme}`"
    :style="cssPropertiesForTheme(theme)"
  >
    <slot name="header">
      <V2Header
        :type="isPdfCapture ? 'simple' : headerType"
        :currentUser="currentUser"
        :currentTheme="currentTheme"
      />
    </slot>

    <div
      class="w-full relative flex-1 flex flex-col"
      data-qa="PageContent"
    >
      <slot/>
    </div>

    <V2Footer
      v-if="footerType"
      :type="footerType"
    />
  </BaseLayout>
</template>
